import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";

import { createAxiosInstance } from "api/axiosConfig";

export const useReferralsPaginatedList = ({ page, patientId, sorting, rows = 10 }) => {
  const tenantVal = localStorage.getItem("kinesin-tenant");

  return useQuery({
    queryKey: ["referralsList", page, patientId, sorting, rows],
    queryFn: async () => {
      const axiosInstance = await createAxiosInstance();
      const response = await axiosInstance.get(
        `${tenantVal}/healthlink/patients/${patientId}/referrals?page=${page}&rows=${rows}&orderBy=${
          sorting === "newest-first" ? "DESC" : "ASC"
        }`
      );

      if (!response?.data?.data) {
        return {
          totalRecords: response?.data?.totalRecords || 0,
          page: response?.data?.page || 1,
          rows: response?.data?.rows || 10,
          data: [],
        };
      }

      const processedData = response.data.data.map((item) => {
        const observationMap = {};
        if (item.referralData?.observations) {
          for (const observation of item.referralData.observations) {
            if (observation.resultsNotes) {
              for (const note of observation.resultsNotes) {
                const identifier = note.obx?.observationIdentifier?.text;
                if (identifier && note.obx?.observationValue !== undefined) {
                  observationMap[identifier] = note.obx.observationValue;
                }
              }
            }
          }
        }

        const contactMap = {};
        if (
          item.referralData?.patientIdentificationSegment?.contactInformation
        ) {
          for (const contact of item.referralData.patientIdentificationSegment
            .contactInformation) {
            if (contact.telecommunicationUseCode) {
              contactMap[contact.telecommunicationUseCode] = {
                telephoneNumber: contact.telephoneNumber,
                emailAddress: contact.emailAddress,
              };
            }
          }
        }

        return {
          item,
          observationMap,
          contactMap,
        };
      });

      return {
        totalRecords: response?.data?.totalRecords || 0,
        page: response?.data?.page || 1,
        rows: response?.data?.rows || 10,
        data: processedData.map(
          ({
            item: {
              referralData: item,
              createdAt,
              id,
              status,
              messageControlId,
              xmlResponseData,
              ackReason,
            },
            observationMap,
            contactMap,
          }) => {
            const typeUnparsed =
              item.referralInformationSegment?.referralType?.text?.toLowerCase() ||
              "";
            const typeParsed =
              typeUnparsed.charAt(0).toUpperCase() + typeUnparsed.slice(1);

            return {
              id: id,
              messageControlId: messageControlId,
              referralName:
                item.providerDataSegments?.[1]?.providerDataSegment
                  ?.providerLocation?.pointOfCare || typeParsed,
              xmlResponseData: xmlResponseData,
              // Why Healthlink refused the latest send, kept on the row since KIN-2152.
              ackReason: ackReason,
              date: createdAt
                ? format(new Date(createdAt), "dd-MM-yyyy")
                : "N/A",
              hospital: item.messageHeaderSegment?.receivingFacility?.id,
              referralType: typeParsed || "",
              status: status,
              gpOutofHoursNumber:
                item.providerDataSegments?.[0]?.providerDataSegment
                  ?.providerCommunicationInformation?.[0]?.telephoneNumber ||
                "N/A",
              familyName:
                item.patientIdentificationSegment?.patientName?.familyName
                  ?.surname || "",
              firstName:
                item.patientIdentificationSegment?.patientName?.givenName || "",
              title:
                item.patientIdentificationSegment?.patientName?.prefix || "",
              gender:
                item.patientIdentificationSegment?.gender === "MALE"
                  ? "MALE"
                  : "FEMALE",
              dateOfBirth:
                item.patientIdentificationSegment?.dateOfBirth?.ts1 || "",
              firstLanguage:
                item.patientIdentificationSegment?.firstLanguage?.text || "",
              wheelchairAssistance:
                observationMap["Physical mobility impairment"] || "No",
              interpreterRequired:
                observationMap["Interpreter Required"] || "No",
              mothersMaidenName: "",
              streetAddress:
                item.patientIdentificationSegment?.address?.streetAddress
                  ?.streetOrMailingAddress || "",
              addressLine2:
                item.patientIdentificationSegment?.address?.otherDesignation ||
                "",
              addressLine3:
                item.patientIdentificationSegment?.address?.city || "",
              addressLine4: "",
              eircode:
                item.patientIdentificationSegment?.address?.zipOrPostal || "",
              mobile:
                contactMap["OTHER_RESIDENCE_NUMBER"]?.telephoneNumber || "",
              phone:
                contactMap["PRIMARY_RESIDENCE_NUMBER"]?.telephoneNumber || "",
              email: contactMap["NETWORK_EMAIL_ADDRESS"]?.emailAddress || "",
              referralPriority:
                item.referralInformationSegment?.referralPriority?.text || "",
              preferredConsultantFamilyName:
                item.providerDataSegments?.[0]?.providerDataSegment
                  ?.providerName?.familyName?.surname || "",
              preferredConsultantFirstName:
                item.providerDataSegments?.[0]?.providerDataSegment
                  ?.providerName?.givenName || "",
              reasonForReferral: observationMap["Reason for Referral"] || "",
              historyOfPresentingComplains:
                observationMap["History of Presenting Complaints"] || "",
              previousHospitalAttendance:
                observationMap["Previous Hospital Attendance"] || "No",
              historyOfPastIllness:
                observationMap["History of Past Illness"] || "",
              historyOfSurgicalProcedures:
                observationMap["History of Surgical Procedures"] || "",
              allergiesAdverseMedicationEvents:
                observationMap["History of allergies"] || "",
              relevantFamilyHistory:
                observationMap["History of Family Member Diseases"] || "",
              additionalInformation:
                observationMap["Additional Relevant Information"] || "",
              currentMedication: "",
              patientOnAnticoagulants:
                observationMap["Anticoagulant Use"] || "No",
              nextOfKin: observationMap["Next of Kin"] || "",
              smoker: observationMap["History of tobacco use"] || "No",
              drinker: observationMap["History of alcohol use"] || "No",
              pulse: observationMap["Pulse"] || "",
              bp1: observationMap["Systolic Blood pressure"] || "",
              bp2: observationMap["Diastolic Blood pressure"] || "",
              bodyHeight: observationMap["Height"] || "",
              weight: observationMap["Weight"] || "",
              bmi: observationMap["Body mass index"] || "",
              laboratoryInvestigation: { name: "N/A" },
              radiologyInvestigation: { name: "N/A" },
              fileAttachment: { name: "N/A" },
            };
          }
        ),
      };
    },
    enabled: !!tenantVal && !!patientId,
    staleTime: 1000 * 60 * 5,
    meta: { errorMessage: "Error fetching referrals" },
  });
};
